import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription, interval } from 'rxjs';
import { Api } from '../../../services/api';

export interface EmailData {
  to: string;
  subject: string;
  body: string;
}

export interface EmailDraft {
  recipients: string[];
  subject: string;
  body: string;
  timestamp: string;
}

@Component({
  selector: 'app-mdl-send-mail',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './mdl-send-mail.html',
  styleUrl: './mdl-send-mail.css'
})
export class MdlSendMail implements OnInit, OnDestroy {
  emailForm: FormGroup;
  recipients: string[] = [];
  recipientInput: string = '';
  characterCount: number = 0;
  showSuccessMessage: boolean = false;
  draftSaved: boolean = false;
  isLoading: boolean = false;

  public api = inject(Api);
  
  private autoSaveSubscription?: Subscription;
  private readonly AUTO_SAVE_INTERVAL = 30000; // 30 segundos

  constructor(
    private fb: FormBuilder,
    // private emailService: EmailService // Inyecta tu servicio de email aquí
  ) {
    this.emailForm = this.createForm();
  }

  ngOnInit(): void {
    this.loadDraft();
    this.setupAutoSave();
    this.updateCharacterCount();
  }

  ngOnDestroy(): void {
    if (this.autoSaveSubscription) {
      this.autoSaveSubscription.unsubscribe();
    }
  }

  private createForm(): FormGroup {
    return this.fb.group({
      subject: ['', [Validators.required, Validators.minLength(3)]],
      body: ['', [Validators.required, Validators.minLength(10)]]
    });
  }

  private setupAutoSave(): void {
    this.autoSaveSubscription = interval(this.AUTO_SAVE_INTERVAL).subscribe(() => {
      this.autoSaveDraft();
    });
  }

  // Gestión de destinatarios
  onRecipientKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addRecipient();
    }
  }

  addRecipient(): void {
    const email = this.recipientInput.trim();
    
    if (this.isValidEmail(email) && !this.recipients.includes(email)) {
      this.recipients.push(email);
      this.recipientInput = '';
      this.autoSaveDraft();
    } else if (!this.isValidEmail(email)) {
      this.showError('Por favor, ingresa un email válido');
    } else if (this.recipients.includes(email)) {
      this.showError('Este email ya está agregado');
    }
  }

  removeRecipient(email: string): void {
    const index = this.recipients.indexOf(email);
    if (index > -1) {
      this.recipients.splice(index, 1);
      this.autoSaveDraft();
    }
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Contador de caracteres
  updateCharacterCount(): void {
    const bodyValue = this.emailForm.get('body')?.value || '';
    this.characterCount = bodyValue.length;
  }

  getCharCountColor(): string {
    if (this.characterCount > 1000) {
      return '#E53E3E';
    } else if (this.characterCount > 500) {
      return '#DD6B20';
    }
    return '#a0aec0';
  }

  // Envío de email
  async sendEmail(): Promise<void> {
    if (this.emailForm.invalid || this.recipients.length === 0) {
      this.validateForm();
      return;
    }

    this.isLoading = true;

    try {
      const emailData: EmailData = {
        to: this.recipients[0],
        subject: this.emailForm.value.subject,
        body: this.emailForm.value.body
      };

      // Aquí harías la llamada real a tu servicio
      await this.api.sendOutlookMail(emailData).subscribe(
        { 
          next: (response: any) => {console.log(response)},
          error: (err: any) => { console.error('Error enviando email:', err); }
        }
      );
      
      this.showSuccessMessage = true;
      setTimeout(() => {
        this.showSuccessMessage = false;
        this.clearForm();
      }, 3000);

    } catch (error) {
      console.error('Error enviando email:', error);
      this.showError('Error al enviar el email. Por favor, intenta nuevamente.');
    } finally {
      this.isLoading = false;
    }
  }

  private async sendEmailToServer(emailData: EmailData): Promise<void> {
    // Simular llamada API
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('Email enviado:', emailData);
        resolve();
      }, 2000);
    });

    // Implementación real:
    // return this.emailService.sendEmail(emailData).toPromise();
  }

  private validateForm(): void {
    if (this.recipients.length === 0) {
      this.showError('Por favor, agrega al menos un destinatario');
      return;
    }

    Object.keys(this.emailForm.controls).forEach(key => {
      const control = this.emailForm.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  // Gestión de borradores
  saveDraft(): void {
    const draft: EmailDraft = {
      recipients: this.recipients,
      subject: this.emailForm.value.subject || '',
      body: this.emailForm.value.body || '',
      timestamp: new Date().toISOString()
    };

    localStorage.setItem('emailDraft', JSON.stringify(draft));
    
    this.draftSaved = true;
    setTimeout(() => {
      this.draftSaved = false;
    }, 2000);
  }

  autoSaveDraft(): void {
    const subject = this.emailForm.value.subject || '';
    const body = this.emailForm.value.body || '';
    
    if (subject.trim() || body.trim() || this.recipients.length > 0) {
      this.saveDraft();
    }
  }

  private loadDraft(): void {
    const saved = localStorage.getItem('emailDraft');
    if (saved) {
      try {
        const draft: EmailDraft = JSON.parse(saved);
        this.recipients = draft.recipients || [];
        this.emailForm.patchValue({
          subject: draft.subject || '',
          body: draft.body || ''
        });
        this.updateCharacterCount();
      } catch (error) {
        console.error('Error cargando borrador:', error);
      }
    }
  }

  // Limpiar formulario
  clearForm(): void {
    this.recipients = [];
    this.recipientInput = '';
    this.emailForm.reset();
    this.characterCount = 0;
    this.draftSaved = false;
    
    // Limpiar borrador guardado
    localStorage.removeItem('emailDraft');
  }

  // Utilidades
  private showError(message: string): void {
    // Implementa tu sistema de notificaciones aquí
    alert(message); // Temporal - reemplaza con tu sistema de toast/snackbar
  }
}